"use client";

import { FunctionComponent, PropsWithChildren, ReactNode, RefObject, createContext, useCallback, useEffect, useMemo, useState } from "react";

export interface ScrollToProviderProps {
    children: ReactNode;
    scrollableRef: RefObject<HTMLElement | null>;
}

export interface ScrollToContextType {
    hasScrolled: boolean;
    scrollTo: (position?: number) => void;
    scrollableRef: RefObject<HTMLElement | null>;
}

export const ScrollToContext = createContext<ScrollToContextType | undefined>(undefined);

export const ScrollToProvider: FunctionComponent<PropsWithChildren<ScrollToProviderProps>> = ({ scrollableRef, children }) => {
    const [hasScrolled, setHasScrolled] = useState(false);

    const scrollTo = useCallback(
        (position = 0) => {
            if (scrollableRef.current) {
                scrollableRef.current.scrollTo({ behavior: "smooth", top: position });
            }
        },
        [scrollableRef],
    );

    useEffect(() => {
        const handleScroll = () => {
            if (scrollableRef.current) {
                setHasScrolled(scrollableRef.current.scrollTop > 0);
            }
        };

        const currentRef = scrollableRef.current;
        currentRef?.addEventListener("scroll", handleScroll);

        return () => {
            currentRef?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollableRef]);

    const value = useMemo(() => ({ hasScrolled, scrollTo, scrollableRef }), [hasScrolled, scrollTo, scrollableRef]);

    return <ScrollToContext.Provider value={value}>{children}</ScrollToContext.Provider>;
};
